import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useChatStore } from '../../store/chatStore';
import './Sidebar.css';
import { MessageCircle, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { SidebarSection } from './SidebarSection';
import { getSectionsByPosition } from './sidebar-config';
import { Tooltip } from '@plerionhq/design-system';
import { PleriLogo } from '../PleriLogo';
import { PleriClient } from '../../client/PleriClient';
import { usePleriRender } from '../../context/PleriRenderContext';
import {
  DocsChatInformationCard,
  FeatureRequestChatInformationCard,
  ContactSupportChatInformationCard,
} from './ChatInformationCards';
import { RecentChatsList } from './RecentChatsList';

import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isCollapsed?: boolean; // For minimized sidebar in full screen
  onToggleCollapsed?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed = false,
  onToggleCollapsed,
}) => {
  const { t } = useTranslation('pleri/chat');
  const navigate = useNavigate();
  const createConversation = useChatStore((state) => state.createConversation);

  const { renderType } = usePleriRender();
  let type: 'full' | 'extension' = 'full';
  if (renderType === 'extension') {
    type = 'extension';
  }

  const handleNewChat = () => {
    const newId = createConversation();
    navigate({
      to: '/c/$conversationId',
      params: { conversationId: newId },
    });
  };

  // Handle support options
  const handleChatWithDocs = () => {
    PleriClient.updateChatInput({
      prompt:
        "Hi Pleri, can you help me understand how a feature works? I'd love to learn more about it using the documentation.",
      useExistingConversation: false,
      autoSubmit: true,
      chatInformation: <DocsChatInformationCard />,
    });
  };

  const handleFeatureRequest = () => {
    PleriClient.updateChatInput({
      prompt:
        'Hi Pleri, I think you could benefit from this feature. Can you help me submit a feature request?',
      useExistingConversation: false,
      autoSubmit: true,
      chatInformation: <FeatureRequestChatInformationCard />,
    });
  };

  const handleContactSupport = () => {
    PleriClient.updateChatInput({
      prompt:
        'Hi Pleri, I encountered an issue with your capability. Can you help me raise a support ticket?',
      useExistingConversation: false,
      autoSubmit: true,
      chatInformation: <ContactSupportChatInformationCard />,
    });
  };

  // Get sections for full sidebar
  const topSections = getSectionsByPosition('top', type);
  const middleSections = getSectionsByPosition('middle', type);
  const bottomSections = getSectionsByPosition('bottom', type);

  // Custom callbacks for specific items
  const itemCallbacks = {
    'new-chat': handleNewChat,
    'chat-with-docs': handleChatWithDocs,
    'feature-request': handleFeatureRequest,
    'contact-support': handleContactSupport,
  };

  return (
    <>
      <div
        className={`sidebar relative flex h-full flex-shrink-0 flex-col overflow-hidden backdrop-blur-sm transition-[width] duration-300 ease-in-out ${
          isCollapsed
            ? 'w-[52px] border-r border-gray-200/80 bg-gray-50 dark:border-gray-700/80 dark:bg-gray-800'
            : 'w-64 border-r border-gray-200/80 bg-gray-50 dark:border-gray-700/80 dark:bg-gray-800'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Collapse/Expand button - Only show when onToggleCollapsed is provided */}
          {onToggleCollapsed && (
            <div
              className={`flex items-center px-2 transition-[padding] duration-300 ease-in-out ${isCollapsed ? 'py-2' : 'py-2 pb-4'}`}
            >
              {/* When collapsed: Show Pleri logo when not hovered, toggle when hovered */}
              {isCollapsed ? (
                <div className="group flex w-full justify-center">
                  <Tooltip label={t('expandSidebar')} placement="right">
                    <button
                      onClick={onToggleCollapsed}
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[4.5px] text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                      data-testid="expand-sidebar-button"
                      aria-label={t('expandSidebar')}
                    >
                      {/* Pleri logo - hidden on group hover */}
                      <div className="group-hover:hidden">
                        <PleriLogo size={32} className="rounded-md" />
                      </div>
                      {/* Toggle icon - shown on group hover */}
                      <PanelLeftOpen className="hidden h-5 w-5 group-hover:block" />
                    </button>
                  </Tooltip>
                </div>
              ) : (
                <>
                  {/* Pleri Logo and Text - only shown when expanded */}
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="flex-shrink-0">
                      <PleriLogo size={32} className="rounded-md" />
                    </div>
                    <span className="text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-gray-100">
                      {t('pleri')}
                    </span>
                  </div>
                  {/* Toggle button - shown when expanded */}
                  <Tooltip label={t('collapseSidebar')} placement="right">
                    <button
                      onClick={onToggleCollapsed}
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[4.5px] text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                      aria-label={t('collapseSidebar')}
                    >
                      <PanelLeftClose className="h-5 w-5" />
                    </button>
                  </Tooltip>
                </>
              )}
            </div>
          )}

          {/* Top Navigation Section */}
          <div
            className={`py-2 transition-[padding] duration-300 ease-in-out ${isCollapsed ? 'px-0' : 'px-2'}`}
          >
            {topSections.map((section) => (
              <SidebarSection
                key={section.id}
                section={section}
                isCollapsed={isCollapsed}
                itemCallbacks={itemCallbacks}
              />
            ))}
          </div>

          {/* Middle Navigation Section - Only show when not collapsed */}
          {!isCollapsed && middleSections.length > 1 && (
            <div className="border-t border-gray-200/80 px-2 py-2 dark:border-gray-700/80">
              {middleSections.map((section) => (
                <SidebarSection
                  key={section.id}
                  section={section}
                  isCollapsed={isCollapsed}
                  itemCallbacks={itemCallbacks}
                />
              ))}
            </div>
          )}

          {/* Recent Chats Section - Only show when not collapsed */}
          {!isCollapsed ? (
            <RecentChatsList size="compact" showSourceFilter={true} />
          ) : onToggleCollapsed ? (
            /* Show Recent Chats toggle button when collapsed */
            <div className="flex-1 border-t border-gray-200/80 px-0 py-2 dark:border-gray-700/80">
              <SidebarSection
                key="collapsed-toggle"
                section={{
                  id: 'collapsed-toggle',
                  position: 'middle',
                  items: [
                    {
                      id: 'toggle-chats',
                      type: 'button',
                      label: t('recentChats'),
                      testId: 'recent-chats-nav-item',
                      icon: MessageCircle,
                      onClick: onToggleCollapsed,
                      tooltipLabel: 'Recent chats',
                      renderType: ['full'],
                    },
                  ],
                }}
                isCollapsed={true}
                itemCallbacks={{}}
              />
            </div>
          ) : null}

          {/* Bottom Section - Settings & Support */}
          <div
            className={`flex-shrink-0 border-t border-gray-200/80 py-2 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] transition-[padding] duration-300 ease-in-out dark:border-gray-700/80 ${isCollapsed ? 'px-0' : 'px-2'}`}
          >
            {bottomSections.map((section) => (
              <SidebarSection
                key={section.id}
                section={section}
                isCollapsed={isCollapsed}
                itemCallbacks={itemCallbacks}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
